import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import { DEFAULT_AGENT_MODEL } from '@troia-v3/types';
import type { AgentDraft } from '../../services/agentDraftApi';

interface PreviewPanelProps {
  draft: AgentDraft | null;
  migrationBlockCount: number;
}

const CAPABILITY_LABELS: Record<string, string> = {
  calendar_management: '📅 Calendário',
  lead_management: '👤 Gestão de Leads',
  voice_interaction: '🎙 Voz',
  database_search: '🗄 Busca em Base',
  transfer_to_human: '🔄 Transferência',
};

const CATEGORY_LABELS: Record<string, string> = {
  sdr: 'SDR (Prospecção)',
  attendant: 'Atendente',
  support: 'Suporte Técnico',
  financial: 'Financeiro',
  sales: 'Vendas',
  scheduling: 'Agendamento',
  other: 'Outro',
};

const SEGMENT_LABELS: Record<string, string> = {
  educational: 'Educacional',
  real_estate: 'Imobiliária',
  healthcare: 'Saúde',
  ecommerce: 'E-commerce',
  services: 'Serviços',
  saas: 'SaaS / Software',
  accounting: 'Contabilidade',
  law: 'Advocacia',
  food: 'Alimentação',
  automotive: 'Automotivo',
  solar_energy: 'Energia Solar',
  apparel: 'Confecção',
  generic: 'Genérico',
};

/**
 * PreviewPanel — Right side of agent creation wizard
 * Shows read-only preview of generated agent draft
 */
export const PreviewPanel: React.FC<PreviewPanelProps> = ({ draft, migrationBlockCount }) => {
  const [isSystemPromptExpanded, setIsSystemPromptExpanded] = useState(false);

  if (!draft) {
    return (
      <div
        className="h-full flex items-center justify-center px-6 py-8"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          color: 'var(--theme-muted-text)',
        }}
      >
        <div className="text-center max-w-sm">
          <Icon
            name="message-square"
            size={32}
            className="mx-auto mb-4"
            style={{ color: 'var(--theme-muted-text)' }}
          />
          <p className="text-sm">
            Descreva seu agente no chat ao lado para ver o preview aqui
          </p>
        </div>
      </div>
    );
  }

  // Get first 3 lines of system prompt for preview
  const systemPromptLines = draft.systemPrompt.split('\n');
  const systemPromptPreview = systemPromptLines.slice(0, 3).join('\n');
  const hasMoreSystemPrompt = systemPromptLines.length > 3;

  return (
    <div
      className="h-full flex flex-col overflow-auto"
      style={{ backgroundColor: 'var(--theme-card-background)' }}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b px-6 py-4" style={{ borderColor: 'var(--theme-card-border)' }}>
        <h2
          className="font-semibold text-lg"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Preview do Agente
        </h2>
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Gerado automaticamente · Editável após criação
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
        {/* Name */}
        <div>
          <p
            className="text-2xl font-bold"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {draft.name}
          </p>
        </div>

        {/* Category + Segment Badges */}
        <div className="flex items-center gap-3">
          <div
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: '#c4b5fd',
              color: '#5b21b6',
            }}
          >
            {CATEGORY_LABELS[draft.category] ?? draft.category}
          </div>
          <div
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: '#bfdbfe',
              color: '#1e40af',
            }}
          >
            {SEGMENT_LABELS[draft.segment] ?? draft.segment}
          </div>
        </div>

        {/* Model */}
        <div>
          <p
            className="text-xs font-medium mb-2"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            MODELO
          </p>
          <p
            className="text-sm"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {/* TEMPORÁRIO (23/10/2026): o agente é salvo sempre no GPT-6 Luna
                (ver AgentCreationDrawer). Para voltar: {draft.model} */}
            {DEFAULT_AGENT_MODEL}
          </p>
        </div>

        {/* Capabilities */}
        {draft.enabledCapabilities.length > 0 && (
          <div>
            <p
              className="text-xs font-medium mb-3"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              CAPACIDADES
            </p>
            <div className="flex flex-wrap gap-2">
              {draft.enabledCapabilities.map((capability) => (
                <div
                  key={capability}
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: '#dcfce7',
                    color: '#166534',
                  }}
                >
                  {CAPABILITY_LABELS[capability] || capability}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System Prompt */}
        <div>
          <p
            className="text-xs font-medium mb-2"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            SYSTEM PROMPT
          </p>
          <div
            className="rounded-lg border p-3 cursor-pointer transition-colors hover:opacity-90"
            style={{
              backgroundColor: '#f3f4f6',
              borderColor: 'var(--theme-card-border)',
            }}
            onClick={() => setIsSystemPromptExpanded(!isSystemPromptExpanded)}
          >
            <pre
              className="text-xs font-mono whitespace-pre-wrap break-words"
              style={{
                color: '#374151',
                maxHeight: isSystemPromptExpanded ? 'none' : '72px',
                overflow: isSystemPromptExpanded ? 'visible' : 'hidden',
                lineHeight: '1.5',
              }}
            >
              {isSystemPromptExpanded ? draft.systemPrompt : systemPromptPreview}
            </pre>
            {hasMoreSystemPrompt && (
              <button
                className="text-xs font-medium mt-2 transition-colors"
                style={{
                  color: '#8b5cf6',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSystemPromptExpanded(!isSystemPromptExpanded);
                }}
              >
                {isSystemPromptExpanded ? 'Menos' : 'Mais'}
              </button>
            )}
          </div>
        </div>

        {/* Suggested Databases — filtro defensivo: items sem nome E sem id são fantasmas (LLM emitiu vazio) */}
        {(() => {
          const renderable = draft.suggestedDatabases.filter(
            (db) => (db.name ?? '').trim().length > 0 || Boolean(db.existingDatabaseId),
          );
          if (renderable.length === 0) return null;
          return (
          <div>
            <p
              className="text-xs font-medium mb-3"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              BASES DE DADOS SUGERIDAS
            </p>
            <div className="space-y-2">
              {renderable.map((db, index) => (
                <div
                  key={index}
                  className="rounded-lg border p-3"
                  style={{
                    backgroundColor: 'var(--theme-page-background)',
                    borderColor: 'var(--theme-card-border)',
                  }}
                >
                  <div className="flex items-start gap-2">
                    <Icon
                      name="database"
                      size={16}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: '#8b5cf6' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        {db.name}
                      </p>
                      <p
                        className="text-xs mt-1"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {db.reason}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Migration Blocks Badge */}
        {migrationBlockCount > 0 && (
          <div
            className="rounded-lg border p-3 flex items-center gap-2"
            style={{
              backgroundColor: '#fffbeb',
              borderColor: '#fbbf24',
            }}
          >
            <Icon
              name="alert-circle"
              size={18}
              style={{ color: '#d97706' }}
            />
            <p
              className="text-sm font-medium"
              style={{ color: '#92400e' }}
            >
              {migrationBlockCount} {migrationBlockCount === 1 ? 'bloco' : 'blocos'} pra organizar
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

PreviewPanel.displayName = 'PreviewPanel';
